import type { Icon } from '@phosphor-icons/react';
import * as React from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { useScrollEdges } from '@/hooks/useScrollEdges';
import { SEGMENT_CHIP_OFF, SEGMENT_CHIP_ON, scrollEdgeFade } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

export interface SegmentedToggleOption<V extends string = string> {
  value: V;
  icon?: React.ComponentType<{ className?: string }> | Icon | React.ReactNode;
  label?: string;
  /** Optional trailing count, rendered as a quiet muted number (not baked
   *  into the label as "· N", which reads at full label weight). */
  count?: number;
  /** Small status glyph rendered after the label/count. It's a status
   *  marker, not a second label, so it stays visually subordinate — the
   *  caller owns the glyph's color/shape, keeping this primitive
   *  color-agnostic (it can't trip the no-hardcoded-colors lint gate). */
  badge?: React.ReactNode;
  /** Muted treatment for a de-emphasised option (e.g. a paused platform).
   *  Composes with active/inactive styling — dimmed is not disabled, the
   *  option stays selectable. */
  dimmed?: boolean;
  /** Hover/focus description. Reuses the `variant="icon"` branch's existing
   *  Tooltip/TooltipProvider convention rather than introducing a second
   *  one, and doubles as the accessible name for icon-only tabs that have
   *  no visible label text. */
  tooltip?: string;
  /** Explicit accessible name, for when the visible label under-describes the
   *  option — a `dimmed` option is the case that forced this: opacity carries
   *  its state to sighted users and to nobody else, and a Radix tooltip is
   *  only associated while it's open. Wins over both `label` and the `tooltip`
   *  fallback; omit it and behavior is exactly as before. */
  ariaLabel?: string;
}

export type SegmentedToggleVariant = 'icon' | 'pills' | 'default' | 'tabs';
export type SegmentedToggleSize = 'sm' | 'md' | 'lg';
/** `'toggle'` (the default) is today's behavior unchanged: `aria-pressed`
 *  buttons that are each independently tabbable — every existing consumer
 *  keeps this with zero change. `'tabs'` opts a consumer into a real
 *  WAI-ARIA tablist (role="tablist"/"tab", aria-selected, roving tabindex,
 *  horizontal-only arrow-key navigation). Orthogonal to `variant`, which
 *  stays purely visual — this only changes ARIA and keyboard behavior. */
export type SegmentedToggleSemantics = 'toggle' | 'tabs';

export interface SegmentedToggleProps<V extends string = string> {
  value: V;
  onValueChange: (value: V) => void;
  options: ReadonlyArray<SegmentedToggleOption<V>>;
  variant?: SegmentedToggleVariant;
  size?: SegmentedToggleSize;
  className?: string;
  semantics?: SegmentedToggleSemantics;
  /** Accessible name for the group when `semantics="tabs"` renders a tablist. */
  ariaLabel?: string;
  /**
   * Stacks the options in a column instead of a row.
   *
   * Added for the concept design workbench, where the platform switcher sits
   * against the right rail's edge under the Design/Preview toggle as a tool
   * rail rather than floating centred over the canvas (founder, 2026-08-21:
   * _"put the platform icons stacked vertically and below design/preview
   * aligned against that border line… more like a toolbar"_). Extending this
   * primitive rather than hand-rolling a column keeps the selected-state
   * grammar, the tooltips and the roving tabindex that a stack of
   * `CircleIconButton`s would have quietly lost.
   *
   * Only meaningful for the `default` and `pills` variants. Vertical stacks
   * take a squared-off radius, since a full pill on a column reads as a
   * lozenge rather than a rail.
   */
  orientation?: 'horizontal' | 'vertical';
  /** `variant="icon"` only: 44px square buttons below `md`, for a strip that is a primary control. */
  touchTargets?: boolean;
  /**
   * Whether the group draws its own enclosing surface.
   *
   * `'none'` (the default) is the ChatGPT / Meetup grammar the founder named
   * on 2026-09-04: the group is NOT a box. Unselected options are plain text
   * on the page's own ground; the selected option alone carries a soft filled
   * chip. Nothing is drawn to say "these belong together" — proximity already
   * says it, and the chip already says which one is on.
   *
   * `'group'` restores the old `control-group-surface` capsule: a bordered
   * tinted box with a raised white chip inside it. It survives ONLY for the
   * case the capsule genuinely earns — a toggle floating over content it does
   * not own (an image canvas, a media overlay), where without a ground the
   * options have no readable ground. It is not a taste option; if the
   * toggle sits on a normal page ground, it is `'none'`.
   *
   * WHY THE DEFAULT FLIPPED: the capsule reads as a two-button *switch*, a
   * heavier object than the thing it actually is — a view selector. On a
   * dense surface (an ad-preview placement switcher, a settings row) that
   * extra weight competed with the content it was meant to filter. Founder,
   * 2026-09-04: _"I feel like we're incorrectly using a group with a
   * background color that looks like a button switch."_
   */
  ground?: 'none' | 'group';
  /**
   * Fade the edge of a horizontally scrolling row that still has more behind it.
   *
   * Without it an overflowing row simply ends mid-label ("Company si"), which reads as a clipping
   * bug rather than "scroll for more". The fade is measured from the row's own scroll geometry
   * (`useScrollEdges`), so a row that fits stays untouched at every width. Only the `default` and
   * `pills` variants in a horizontal row scroll; pair with `className="max-w-full"` so the row is
   * bounded by its container.
   */
  scrollFade?: boolean;
}

const ICON_SIZES: Record<SegmentedToggleSize, string> = {
  sm: 'size-3',
  md: 'size-3.5',
  lg: 'size-4',
};

function renderIcon(icon: SegmentedToggleOption['icon'], iconClass: string): React.ReactNode {
  if (!icon) return null;
  if (React.isValidElement(icon)) {
    const props = (icon as React.ReactElement).props as { className?: string };
    return React.cloneElement(
      icon as React.ReactElement,
      {
        className: cn(iconClass, props?.className),
      } as Record<string, unknown>,
    );
  }
  if (typeof icon === 'string' || typeof icon === 'number') {
    return <span className={iconClass}>{icon}</span>;
  }
  if (typeof icon === 'function' || (typeof icon === 'object' && icon !== null)) {
    const IconComp = icon as React.ComponentType<{ className?: string }>;
    return <IconComp className={iconClass} />;
  }
  return null;
}

export function SegmentedToggle<V extends string = string>({
  value,
  onValueChange,
  options,
  variant = 'icon',
  size = 'md',
  className,
  semantics = 'toggle',
  ariaLabel,
  orientation = 'horizontal',
  ground = 'none',
  scrollFade = false,
  touchTargets = false,
}: SegmentedToggleProps<V>): JSX.Element {
  const isTablist = semantics === 'tabs';
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const scrollEdges = useScrollEdges(
    scrollRef,
    scrollFade,
    options.map((option) => option.value).join('|'),
  );
  const hasGround = ground === 'group';
  const buttonRefs = React.useRef<Array<HTMLButtonElement | null>>([]);

  // Roving-tabindex target. Falls back to the first option when `value`
  // matches none of them (e.g. a transient render before the option list
  // resolves, or the previously-active option was just removed) — without
  // this fallback every tab gets tabIndex=-1 and the whole strip drops out
  // of the keyboard tab order with no way back in short of a mouse click.
  const selectedIndex = options.findIndex((option) => option.value === value);
  const rovingIndex = selectedIndex >= 0 ? selectedIndex : 0;

  const registerRef = (index: number) => (el: HTMLButtonElement | null) => {
    buttonRefs.current[index] = el;
  };

  // Roving-tabindex navigation, horizontal axis only. Up/Down are
  // deliberately left unhandled — a consumer of this primitive uses arrow
  // keys elsewhere on the vertical axis for a different purpose, so this
  // only claims the axis a tablist actually owns.
  const handleKeyDown = (event: React.KeyboardEvent<HTMLButtonElement>, index: number) => {
    if (!isTablist) return;
    // The axis follows the layout: a vertical tablist that answered Left/Right
    // would be unreachable by the arrows a person actually reaches for.
    const nextKey = orientation === 'vertical' ? 'ArrowDown' : 'ArrowRight';
    const prevKey = orientation === 'vertical' ? 'ArrowUp' : 'ArrowLeft';
    let nextIndex: number;
    switch (event.key) {
      case nextKey:
        nextIndex = (index + 1) % options.length;
        break;
      case prevKey:
        nextIndex = (index - 1 + options.length) % options.length;
        break;
      case 'Home':
        nextIndex = 0;
        break;
      case 'End':
        nextIndex = options.length - 1;
        break;
      default:
        return;
    }
    event.preventDefault();
    onValueChange(options[nextIndex].value);
    buttonRefs.current[nextIndex]?.focus();
  };

  // Shared role/selection wiring: 'toggle' preserves today's aria-pressed
  // markup byte-for-byte; 'tabs' swaps in role="tab"/aria-selected plus a
  // roving tabindex so only the selected tab sits in the natural tab order.
  const semanticProps = (active: boolean) =>
    isTablist
      ? ({ role: 'tab', 'aria-selected': active } as const)
      : ({ 'aria-pressed': active } as const);

  // Accessible name for an option with no visible label text (icon-only
  // tabs/pills). Falls back to the tooltip description, matching the
  // existing icon-variant convention rather than inventing a new one.
  const accessibleLabel = (option: SegmentedToggleOption<V>) =>
    option.ariaLabel ?? (option.label ? undefined : (option.tooltip ?? undefined));

  const wrapWithTooltip = (
    option: SegmentedToggleOption<V>,
    node: React.ReactElement,
  ): React.ReactElement =>
    option.tooltip ? (
      <Tooltip key={option.value}>
        <TooltipTrigger asChild>{node}</TooltipTrigger>
        <TooltipContent>{option.tooltip}</TooltipContent>
      </Tooltip>
    ) : (
      React.cloneElement(node, { key: option.value })
    );

  if (variant === 'icon') {
    // Tightened on mobile: 36px container / 28px buttons (was 44px / 40px).
    // The 44px touch-target rule applies to PRIMARY targets — for an inline
    // secondary view-mode toggle peer-to a tabs row + counter, 44px reads
    // as oversized chrome (Linear / Notion / ChatGPT all use ~28–32px here).
    return (
      <TooltipProvider delayDuration={200}>
        <div
          role={isTablist ? 'tablist' : undefined}
          aria-label={isTablist ? ariaLabel : undefined}
          className={cn(
            touchTargets
              ? 'inline-flex h-11 items-center rounded-full md:h-8'
              : 'inline-flex h-9 items-center rounded-full md:h-8',
            hasGround ? 'control-group-surface p-0.5' : 'gap-0.5',
            className,
          )}
        >
          {options.map((option, index) => {
            const active = option.value === value;
            const name = option.ariaLabel ?? option.tooltip ?? option.label;
            return (
              <Tooltip key={option.value}>
                <TooltipTrigger asChild>
                  <button
                    ref={registerRef(index)}
                    type="button"
                    aria-label={name}
                    {...semanticProps(active)}
                    tabIndex={isTablist ? (index === rovingIndex ? 0 : -1) : undefined}
                    onClick={() => onValueChange(option.value)}
                    onKeyDown={(event) => handleKeyDown(event, index)}
                    className={cn(
                      touchTargets ? 'size-11 md:size-7' : 'size-8 md:size-7',
                      'rounded-full inline-flex items-center justify-center transition',
                      active
                        ? hasGround
                          ? 'bg-background text-foreground shadow-elevation-1'
                          : SEGMENT_CHIP_ON
                        : SEGMENT_CHIP_OFF,
                      option.dimmed && 'opacity-50',
                    )}
                  >
                    {renderIcon(option.icon, 'size-4 md:size-3.5')}
                  </button>
                </TooltipTrigger>
                <TooltipContent>{name}</TooltipContent>
              </Tooltip>
            );
          })}
        </div>
      </TooltipProvider>
    );
  }

  const iconClass = cn(ICON_SIZES[size], 'transition-colors duration-200');

  if (variant === 'tabs') {
    return (
      <TooltipProvider delayDuration={200}>
        <div
          role={isTablist ? 'tablist' : undefined}
          aria-label={isTablist ? ariaLabel : undefined}
          className={cn('inline-flex items-center gap-6 overflow-x-auto scrollbar-hide', className)}
        >
          {options.map((option, index) => {
            const active = option.value === value;
            const buttonClass = cn(
              'flex items-center h-auto transition duration-200 relative whitespace-nowrap rounded-none border-b-2',
              size === 'sm'
                ? 'px-0 py-2.5 text-sm md:py-1.5 md:text-xs font-medium gap-2 md:gap-1.5'
                : 'px-0 py-3 text-base md:py-2 md:text-sm font-medium gap-2.5 md:gap-2',
              active
                ? 'text-foreground border-foreground'
                : 'text-muted-foreground border-transparent hover:text-foreground',
              option.dimmed && 'opacity-50',
            );
            const button = (
              <button
                ref={registerRef(index)}
                type="button"
                aria-label={accessibleLabel(option)}
                {...semanticProps(active)}
                tabIndex={isTablist ? (index === rovingIndex ? 0 : -1) : undefined}
                onClick={() => onValueChange(option.value)}
                onKeyDown={(event) => handleKeyDown(event, index)}
                className={buttonClass}
              >
                {renderIcon(
                  option.icon,
                  cn(iconClass, active ? 'text-foreground' : 'text-muted-foreground'),
                )}
                {option.label && <span>{option.label}</span>}
                {typeof option.count === 'number' && (
                  <span
                    className={cn(
                      'text-detail font-normal tabular-nums',
                      active ? 'text-muted-foreground' : 'text-muted-foreground/50',
                    )}
                  >
                    {option.count}
                  </span>
                )}
                {option.badge}
              </button>
            );
            return wrapWithTooltip(option, button);
          })}
        </div>
      </TooltipProvider>
    );
  }

  const isVertical = orientation === 'vertical';
  // Below md a 28px segment is under the 44px touch target. The target is grown
  // invisibly (a ::before 8px taller each side, so 28 + 16 = 44) and the row
  // takes matching padding and negative margin so the layout never moves. Only
  // the vertical axis grows, so neighbouring segments never overlap. It does not
  // apply to the sm size (already 44px tall on mobile), to a vertical stack
  // (the extra height would overlap the segment above and below) or to a
  // grounded capsule (its own surface would have to grow).
  const mobileHitArea = size !== 'sm' && !isVertical && !hasGround;
  // Groundless is the default (see the `ground` prop's note). The capsule's
  // inner padding goes with the capsule: without a box to sit inside, `p-1`
  // is just an unexplained inset that pushes the first option off the
  // alignment line every other control on the row shares.
  const containerClass = isVertical
    ? cn(
        'inline-flex flex-col items-stretch gap-0.5 rounded-2xl',
        hasGround && 'control-group-surface p-0.5',
      )
    : variant === 'pills'
      ? cn(
          'inline-flex w-full items-center gap-0.5 overflow-x-auto rounded-full scrollbar-hide sm:w-fit',
          hasGround && 'control-group-surface p-1',
          mobileHitArea && 'max-md:-my-2 max-md:py-2',
        )
      : cn(
          'inline-flex items-center overflow-x-auto rounded-full scrollbar-hide',
          hasGround ? 'control-group-surface p-0.5' : 'gap-0.5',
          mobileHitArea && 'max-md:-my-2 max-md:py-2',
        );

  return (
    <TooltipProvider delayDuration={200}>
      <div
        ref={scrollRef}
        role={isTablist ? 'tablist' : undefined}
        aria-label={isTablist ? ariaLabel : undefined}
        className={cn(
          containerClass,
          scrollFade && !isVertical && scrollEdgeFade(scrollEdges),
          className,
        )}
      >
        {options.map((option, index) => {
          const active = option.value === value;
          const buttonClass = cn(
            // font-medium, not -semibold: this pill's own bg + border +
            // shadow-elevation-1 already carries the "selected" signal (same
            // as SELECTABLE_PILL's border/bg pairing in ui-tokens.ts), so the
            // extra weight was redundant emphasis stacked on top of a state
            // already legible from color alone. Height/padding tightened one
            // notch for the same reason SELECTABLE_PILL sits at px-3 py-1.5,
            // not py-2 — a short 1-2 word label doesn't need 44px-scale
            // chrome to stay a comfortable click target.
            'flex h-auto items-center justify-center whitespace-nowrap font-medium transition duration-200',
            isVertical ? 'rounded-xl' : 'rounded-full',
            'focus:outline-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            size === 'sm' ? 'h-11 px-2.5 text-xs gap-1 md:h-6' : 'h-7 px-3 text-xs gap-2',
            mobileHitArea &&
              "relative before:absolute before:inset-x-0 before:-inset-y-2 before:content-[''] before:md:hidden",
            // Groundless selection is a FILL, not a raised card: with no
            // capsule behind it there is nothing to be raised off, so the
            // border + shadow that sold "lifted chip inside a well" become
            // an outlined button — the exact "looks like a button switch"
            // reading this change exists to remove.
            active
              ? hasGround
                ? 'border border-sidebar-border/80 bg-background text-foreground shadow-elevation-1'
                : cn('border-transparent', SEGMENT_CHIP_ON)
              : cn('border-transparent', SEGMENT_CHIP_OFF),
            option.dimmed && 'opacity-50',
          );
          const button = (
            <button
              ref={registerRef(index)}
              type="button"
              aria-label={accessibleLabel(option)}
              {...semanticProps(active)}
              tabIndex={isTablist ? (index === rovingIndex ? 0 : -1) : undefined}
              onClick={() => onValueChange(option.value)}
              onKeyDown={(event) => handleKeyDown(event, index)}
              className={buttonClass}
            >
              {renderIcon(
                option.icon,
                cn(iconClass, active ? 'text-foreground' : 'text-muted-foreground'),
              )}
              {option.label && (
                <span className={cn(variant === 'pills' && option.icon && 'hidden sm:inline')}>
                  {option.label}
                </span>
              )}
              {/* Counts are not exclusive to the underline `tabs` variant.
                  A filter row that says how much sits behind each option
                  ("Waiting on client 4") is the same object whichever way the
                  selection is drawn, and a consumer that wanted both a chip
                  and a count previously had to fall back to a hand-rolled
                  strip — which is how `/reach/approvals` ended up with a
                  second underline strip stacked directly under the route's
                  own. Subordinate weight, matching the `tabs` branch. */}
              {typeof option.count === 'number' && (
                <span
                  className={cn(
                    'text-detail font-normal tabular-nums',
                    active ? 'text-foreground/75' : 'text-muted-foreground',
                  )}
                >
                  {option.count}
                </span>
              )}
              {option.badge}
            </button>
          );
          return wrapWithTooltip(option, button);
        })}
      </div>
    </TooltipProvider>
  );
}
